/**
 * setInterval for async work that must never overlap with itself.
 *
 * The SOP screens poll /api/analysis/results while a generation runs, and that
 * endpoint is at its slowest precisely while a generation is in progress. A
 * plain setInterval does not care whether the previous tick came back — it
 * issues a fresh overlapping request every few seconds regardless. Those pile
 * up against the browser's per-origin connection limit, which is shared across
 * every tab on the same origin, so unrelated API calls end up queued in the
 * browser with no response and no error. That is what "the backend hung during
 * concurrent Enhanced SOP generation" actually looked like from the outside.
 *
 * With this wrapper a tick that arrives while the previous one is still running
 * is skipped, so at most one request per poller is ever outstanding.
 */
export function setNonOverlappingInterval(
  tick: () => Promise<void>,
  ms: number
): ReturnType<typeof setInterval> {
  let inFlight = false;

  return setInterval(async () => {
    if (inFlight) return;
    inFlight = true;
    try {
      await tick();
    } catch (err) {
      // setInterval discards the promise this callback returns, so a rejection
      // here would surface as an unhandled rejection rather than reaching the
      // caller. Log it and carry on: a single failed poll should not take the
      // page down, and the next tick will try again.
      console.error('Polling tick failed:', err);
    } finally {
      // Reset even when the tick throws, otherwise one failed poll would
      // silently stop the poller for the rest of the session.
      inFlight = false;
    }
  }, ms);
}
